<template>
  <div class="lifecycle-periods">
    <section
      v-for="period in resolvedPeriods"
      :key="period.key"
      class="lifecycle-period"
      :style="{ borderLeftColor: period.color }"
    >
      <div class="lifecycle-period-header">
        <div class="flex align-items-center gap-2">
          <button
            v-if="canCollapsePeriods"
            type="button"
            class="lifecycle-period-toggle"
            @click="togglePeriod(period.key)"
          >
            <a-icon :type="period.expanded ? 'down' : 'right'" />
          </button>
          <a-tag class="m-0" :color="period.color">{{ period.title }}</a-tag>
          <span v-if="period.dateRange" class="lifecycle-period-range">{{ period.dateRange }}</span>
        </div>
      </div>

      <template v-if="period.expanded">
        <profile-lifecycle-event-step
          v-for="(step, stepIndex) in period.steps"
          :key="step.key"
          :step="step"
          :is-last="stepIndex === period.steps.length - 1"
          @run-action="runAction"
        />
      </template>
    </section>
  </div>
</template>

<script>
import { Icon, Tag } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import LifecycleEventStep from './lifecycle-event-step.vue'
import lifecyclePeriodsMixin from './lifecycle-periods.mixin'
import lifecycleActionsMixin from './lifecycle-actions.mixin'

const { mapState: profileState } = createNamespacedHelpers('profile')

export default {
  name: 'ProfileLifecyclePeriods',
  mixins: [lifecyclePeriodsMixin, lifecycleActionsMixin],
  components: {
    'a-icon': Icon,
    'a-tag': Tag,
    'profile-lifecycle-event-step': LifecycleEventStep
  },
  props: {
    lifecycle: {
      type: Array,
      default: () => []
    },
    companyFlow: {
      type: Object,
      default: () => ({})
    },
    dynamicFormAvailability: {
      type: Object,
      default: () => ({})
    },
    signedDocuments: {
      type: Array,
      default: () => []
    },
    firstShiftDate: {
      type: String,
      default: undefined
    },
    seniorityDate: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      expandedPeriodKeys: []
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),

    canCollapsePeriods() {
      return this.lifecycle.length > 1
    },

    resolvedPeriods() {
      return this.buildLifecyclePeriods()
    }
  },
  watch: {
    lifecycle: {
      handler() {
        this.resetExpandedPeriods()
      },
      immediate: true
    }
  },
  methods: {
    resetExpandedPeriods() {
      if (!this.canCollapsePeriods) {
        this.expandedPeriodKeys = []

        return
      }

      this.expandedPeriodKeys = [this.getPeriodKey(this.lifecycle.length - 1)]
    },

    togglePeriod(periodKey) {
      if (!this.canCollapsePeriods) return

      if (this.expandedPeriodKeys.includes(periodKey)) {
        this.expandedPeriodKeys = this.expandedPeriodKeys.filter(key => key !== periodKey)

        return
      }

      this.expandedPeriodKeys = [...this.expandedPeriodKeys, periodKey]
    }
  }
}
</script>

<style scoped>
.lifecycle-periods {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.lifecycle-period {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-left: 12px;
  border-left: 1px solid;
}

.lifecycle-period-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.lifecycle-period-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  color: var(--text-color);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.lifecycle-period-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.lifecycle-period-range {
  color: var(--text-color-secondary);
}

@media screen and (max-width: 640px) {
  .lifecycle-period-header {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
